import { ComponentType } from 'react';

import { styled } from '@mui/material';
import clsx from 'clsx';

import { Icons } from '@cast/design-system';
import { Tendency } from '@cast/shared-types';

import { TendencyStableIcon } from '../TendencyStableIcon';

/**
 * @deprecated Use `TendencyIconProps` from `@cast/design-system`
 * (components-v2) instead.
 */
export type TendencyIconProps = {
  variant: 'arrow' | 'caret';
  className?: string;
  tendency: Tendency;
  size?: number;
  invertColors?: boolean;
  colors?: Partial<Record<Tendency, string>>;
  testId?: string;
};

type IconProps = {
  className: string;
  'data-testid'?: string;
  'data-tendency'?: string;
};

const arrowMap: Record<Tendency, ComponentType<IconProps>> = {
  up: (props) => <Icons.ArrowUpRight {...props} />,
  down: (props) => <Icons.ArrowDownRight {...props} />,
  stable: TendencyStableIcon,
};

const caretMap: Record<Tendency, ComponentType<IconProps>> = {
  up: (props) => <Icons.CaretUp {...props} weight="fill" />,
  down: (props) => <Icons.CaretDown {...props} weight="fill" />,
  stable: (props) => <Icons.Minus {...props} />,
};

/**
 * @deprecated Use `TendencyIcon` from `@cast/design-system` (components-v2)
 * instead. Migration notes:
 * - Color semantics are flipped relative to the new default:
 *   no `invertColors` (up = green) ⇔ pass `upIsPositive`;
 *   `invertColors` (up = red) ⇔ new default (omit the prop).
 * - Numeric `size` maps to named sizes: 14px (old default) → `lg`,
 *   16px → `xl` (also `xs` = 8, `sm` = 10, `md` = 12).
 * - The `colors` prop is removed — override via `sx`.
 */
export const TendencyIcon = styled(
  ({
    tendency,
    className,
    invertColors,
    variant,
    colors,
    testId,
  }: TendencyIconProps) => {
    const Icon = variant === 'arrow' ? arrowMap[tendency] : caretMap[tendency];
    if (Icon) {
      return (
        <Icon
          className={clsx(
            className,
            tendency,
            invertColors && 'TendencyIcon-invertColors',
            colors && 'TendencyIcon-customColors'
          )}
          data-testid={testId}
          data-tendency={tendency}
        />
      );
    }
    return null;
  }
)(({ theme, size = 14, colors }) => ({
  fontSize: size,
  '&.up': {
    color: theme.palette.green[500],
  },
  '&.down': {
    color: theme.palette.red[500],
  },
  '&.TendencyIcon-invertColors': {
    '&.up': {
      color: theme.palette.red[500],
    },
    '&.down': {
      color: theme.palette.green[500],
    },
  },
  '&.stable': {
    color: theme.palette.grey[200],
  },
  '&.TendencyIcon-customColors': {
    '&.up': {
      color: colors?.up,
    },
    '&.down': {
      color: colors?.down,
    },
    '&.stable': {
      color: colors?.stable,
    },
  },
}));
TendencyIcon.displayName = 'TendencyIcon';
